import React, { useState } from "react";
import { StatusUpdateScope } from "tekkr-common/dist/model/project/chat";
import { DateTime } from "luxon";
import { type DateRange } from "react-day-picker";
import { ArrowRight } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Calendar } from "../../../components/ui/calendar";

type ScopeType = "days-7" | "days-14" | "since_last_update" | "since_project_start" | "custom";

interface ScopeSelectorProps {
    onStartDraft: (scope: StatusUpdateScope) => void;
    lastUpdateDate: string | null;
    projectCreatedAt: string;
}

const scopeLabels: Record<ScopeType, string> = {
    "days-7": "Last 7 days",
    "days-14": "Last 14 days",
    since_last_update: "Since last update",
    since_project_start: "Since project start",
    custom: "Custom range",
};

export function ScopeSelector({ onStartDraft, lastUpdateDate, projectCreatedAt }: ScopeSelectorProps) {
    const [scope, setScope] = useState<ScopeType>("days-7");
    const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined);
    const [dialogOpen, setDialogOpen] = useState(false);

    const isCustom = scope === "custom";
    const canDraft = !isCustom || (customDateRange?.from && customDateRange?.to);

    const handleScopeChange = (newScope: ScopeType) => {
        setScope(newScope);
        if (newScope === "custom" && !customDateRange?.from) {
            setDialogOpen(true);
        }
    };

    const formatShortDate = (isoDate: string) =>
        DateTime.fromISO(isoDate).toLocaleString({ month: "short", day: "numeric" });

    const handleDraft = () => {
        let statusScope: StatusUpdateScope;

        switch (scope) {
            case "days-7":
                statusScope = { type: "days", days: 7 };
                break;
            case "days-14":
                statusScope = { type: "days", days: 14 };
                break;
            case "since_last_update":
                statusScope = { type: "since_last_update" };
                break;
            case "since_project_start":
                statusScope = { type: "since_project_start" };
                break;
            case "custom":
                if (!customDateRange?.from || !customDateRange?.to) return;
                statusScope = {
                    type: "custom",
                    startDate: DateTime.fromJSDate(customDateRange.from).toISO()!,
                    endDate: DateTime.fromJSDate(customDateRange.to).endOf("day").toISO()!,
                };
                break;
        }

        onStartDraft(statusScope);
    };

    const formatDateRange = () => {
        if (!customDateRange?.from || !customDateRange?.to) return null;
        const from = DateTime.fromJSDate(customDateRange.from).toLocaleString({
            month: "short",
            day: "numeric",
            year: "numeric",
        });
        const to = DateTime.fromJSDate(customDateRange.to).toLocaleString({
            month: "short",
            day: "numeric",
            year: "numeric",
        });
        return `${from} → ${to}`;
    };

    const handleDialogConfirm = () => {
        if (customDateRange?.from && customDateRange?.to) {
            setDialogOpen(false);
        }
    };

    return (
        <div className="flex items-center justify-center pt-8">
            <div className="flex w-full flex-col items-center gap-5 rounded-xl border bg-muted/30 p-12">
                <h3 className="text-lg font-medium">Ready to update your stakeholders on project progress?</h3>

                <div className="flex flex-col items-center gap-4">
                    <p className="font-medium text-muted-foreground">How far back should we look?</p>
                    <Select value={scope} onValueChange={(v) => handleScopeChange(v as ScopeType)}>
                        <SelectTrigger className="w-[240px]">
                            <SelectValue>
                                {isCustom && customDateRange?.from && customDateRange?.to
                                    ? formatDateRange()
                                    : scopeLabels[scope]}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="days-7">Last 7 days</SelectItem>
                            <SelectItem value="days-14">Last 14 days</SelectItem>
                            <SelectItem value="since_last_update">
                                <div className="flex w-full items-center justify-between gap-4">
                                    <span>Since last update</span>
                                    {lastUpdateDate && (
                                        <span className="text-muted-foreground">{formatShortDate(lastUpdateDate)}</span>
                                    )}
                                </div>
                            </SelectItem>
                            <SelectItem value="since_project_start">
                                <div className="flex w-full items-center justify-between gap-4">
                                    <span>Since project start</span>
                                    <span className="text-muted-foreground">{formatShortDate(projectCreatedAt)}</span>
                                </div>
                            </SelectItem>
                            <SelectSeparator />
                            <SelectItem value="custom">Custom range...</SelectItem>
                        </SelectContent>
                    </Select>

                    <Button onClick={handleDraft} disabled={!canDraft}>
                        Draft Update
                        <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                </div>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-fit">
                    <DialogHeader>
                        <DialogTitle>Select date range</DialogTitle>
                    </DialogHeader>
                    <Calendar
                        mode="range"
                        defaultMonth={customDateRange?.from}
                        selected={customDateRange}
                        onSelect={setCustomDateRange}
                        numberOfMonths={2}
                        fromDate={new Date(projectCreatedAt)}
                        toDate={new Date()}
                    />
                    <DialogFooter>
                        <Button onClick={handleDialogConfirm} disabled={!customDateRange?.from || !customDateRange?.to}>
                            Confirm
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
